import React, { useEffect, useState } from "react";
import axiosInstance from "../../axiosInstance";
import { URL } from "../../api.jsx";
import { useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const WalletTransactions = () => {
  const navigate = useNavigate();

  /* ===================== STATE ===================== */
  const [transactions, setTransactions] = useState([]);
  const [companyDropdown, setCompanyDropdown] = useState([]);
  const [branchDropdown, setBranchDropdown] = useState([]);

  /* ===================== FILTER STATE ===================== */
  const [selectedCompany, setSelectedCompany] = useState("");
  const [selectedBranch, setSelectedBranch] = useState("");

  /* ===================== PAGINATION ===================== */
  const [currentPage, setCurrentPage] = useState(0); // backend is 0-based
  const [totalPages, setTotalPages] = useState(0);
  const [totalRecords, setTotalRecords] = useState(0);
  const rowsPerPage = 10;

  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  /* ===================== LOCAL STORAGE ===================== */
  const isAdmin = localStorage.getItem("isAdmin");
  const lsCompanyCode = localStorage.getItem("companyCode");
  const lsBranchCode = localStorage.getItem("branch_code");

  /* ===================== HELPERS ===================== */
  const getCompanyName = (code) => {
    const c = companyDropdown.find((x) => x.id === code);
    return c ? c.value : code || "-";
  };

  const getBranchName = (branchCode, companyCode) => {
    const b = branchDropdown.find(
      (x) => x.branchCode === branchCode && x.companyCode === companyCode
    );
    return b ? b.branchName : branchCode || "-";
  };

  const formatAmount = (val) =>
    val == null ? "-" : `₹ ${Number(val).toLocaleString("en-IN")}`;

  /* ===================== BUILD PARAMS ===================== */
  // Admin can filter freely; non-admin is locked to their own company/branch
  const buildParams = (page) => {
    const params = new URLSearchParams();

    if (isAdmin === "1") {
      if (selectedCompany) params.append("companyCode", selectedCompany);
      if (selectedBranch) params.append("branchCode", selectedBranch);
    } else {
      if (lsCompanyCode) params.append("companyCode", lsCompanyCode);
      if (lsBranchCode && lsBranchCode !== "null")
        params.append("branchCode", lsBranchCode);
    }

    params.append("page", page);
    params.append("size", rowsPerPage);
    return params;
  };

  /* ===================== FETCH DROPDOWNS ===================== */
  const fetchCompanyDropdown = async () => {
    try {
      const res = await axiosInstance.get(URL.COMPANY_LIST_BY_CODE);
      setCompanyDropdown(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error("Company dropdown error:", err);
    }
  };

  const fetchBranchDropdown = async () => {
    try {
      const res = await axiosInstance.get(URL.COMPANY_BRANCH);
      setBranchDropdown(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error("Branch dropdown error:", err);
    }
  };

  /* ===================== FETCH TRANSACTIONS ===================== */
  const fetchTransactions = async (page = 0) => {
    try {
      setLoading(true);
      const params = buildParams(page);
      const res = await axiosInstance.get(
        `${URL.WALLET_TRANSACTIONS}?${params.toString()}`
      );

      if (res.data?.data && Array.isArray(res.data.data)) {
        setTransactions(res.data.data);
        setTotalPages(res.data.totalPages || 0);
        setTotalRecords(res.data.totalRecords || 0);
        setCurrentPage(res.data.currentPage ?? page);
      } else {
        setTransactions([]);
        setTotalPages(0);
        setTotalRecords(0);
      }
    } catch (err) {
      console.error("Fetch transactions error:", err);
      setTransactions([]);
      toast.error("Failed to fetch transactions");
    } finally {
      setLoading(false);
    }
  };

  /* ===================== EXPORT EXCEL ===================== */
  const exportToExcel = async () => {
    try {
      setExporting(true);
      const params = buildParams(0);
      params.delete("page");
      params.delete("size");

      const res = await axiosInstance.get(
        `${URL.WALLET_TRANSACTIONS_EXPORT}?${params.toString()}`,
        { responseType: "blob" }
      );

      const blob = new Blob([res.data], {
        type: "application/octet-stream",
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `wallet_transactions_${new Date().toISOString().split("T")[0]}.xlsx`
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Export error:", err);
      toast.error("Failed to export transactions");
    } finally {
      setExporting(false);
    }
  };

  /* ===================== INIT ===================== */
  useEffect(() => {
    fetchCompanyDropdown();
    fetchBranchDropdown();
    fetchTransactions(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ===================== HANDLERS ===================== */
  const handleSearch = () => fetchTransactions(0);
  const handleReset = () => {
    setSelectedCompany("");
    setSelectedBranch("");
    setTimeout(() => fetchTransactions(0), 0);
  };

  return (
    <div className="container">
      {/* ===================== HEADER ===================== */}
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h3>Wallet Transaction History</h3>
        <button
          className="btn btn-sm btn-outline-secondary"
          onClick={() => navigate(-1)}
        >
          ← Back
        </button>
      </div>

      {/* ===================== FILTER SECTION ===================== */}
      <div className="card p-3 mb-3">
        <div className="d-flex justify-content-between align-items-end flex-wrap gap-2">
          <div className="row gx-2 gy-2 align-items-end flex-grow-1">
            {/* Company filter — admin only */}
            {isAdmin === "1" && (
              <>
                <div className="col-auto">
                  <label className="form-label">Company</label>
                  <select
                    className="form-select form-select-sm"
                    value={selectedCompany}
                    onChange={(e) => {
                      setSelectedCompany(e.target.value);
                      setSelectedBranch("");
                    }}
                  >
                    <option value="">All</option>
                    {companyDropdown.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.value}
                      </option>
                    ))}
                  </select>
                </div>

                {selectedCompany && (
                  <div className="col-auto">
                    <label className="form-label">Agency</label>
                    <select
                      className="form-select form-select-sm"
                      value={selectedBranch}
                      onChange={(e) => setSelectedBranch(e.target.value)}
                    >
                      <option value="">All</option>
                      {branchDropdown
                        .filter(
                          (b) =>
                            String(b.companyCode) === String(selectedCompany)
                        )
                        .map((b) => (
                          <option key={b.branchCode} value={b.branchCode}>
                            {b.branchName}
                          </option>
                        ))}
                    </select>
                  </div>
                )}

                <div className="col-auto">
                  <button
                    className="btn btn-sm btn-primary"
                    onClick={handleSearch}
                  >
                    Search
                  </button>
                </div>

                <div className="col-auto">
                  <button
                    className="btn btn-sm btn-secondary"
                    onClick={handleReset}
                  >
                    Reset
                  </button>
                </div>
              </>
            )}
          </div>

          <button
            className="btn btn-sm btn-success"
            onClick={exportToExcel}
            disabled={exporting}
          >
            {exporting ? "Exporting..." : "Export Excel"}
          </button>
        </div>
      </div>

      {/* ===================== TABLE ===================== */}
      <div
        className="table-responsive"
        style={{ overflowX: "auto", whiteSpace: "nowrap" }}
      >
        <table className="table tablestyle table-sm table-bordered table-hover align-middle">
          <thead className="table-dark">
            <tr>
              <th>S.No.</th>
              <th>Txn ID</th>
              <th>Company</th>
              <th>Agency</th>
              <th>Amount</th>
              <th>Type</th>
              <th>Source</th>
              <th>Company Before</th>
              <th>Company After</th>
              <th>Agency Before</th>
              <th>Agency After</th>
              <th>UTR</th>
              <th>Reference ID</th>
              <th>Remarks</th>
              <th>Created At</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="15" className="text-center text-muted">
                  Loading...
                </td>
              </tr>
            ) : transactions.length > 0 ? (
              transactions.map((t, i) => (
                <tr key={t.id || i}>
                  <td>{currentPage * rowsPerPage + i + 1}</td>
                  <td>{t.id}</td>
                  <td>{getCompanyName(t.companyCode)}</td>
                  <td>
                    {t.branchCode
                      ? getBranchName(t.branchCode, t.companyCode)
                      : "-"}
                  </td>
                  <td>{formatAmount(t.amount)}</td>
                  <td>
                    <span
                      className={`badge rounded-pill ${
                        t.transactionType === "CREDIT"
                          ? "bg-success"
                          : "bg-danger"
                      }`}
                    >
                      {t.transactionType}
                    </span>
                  </td>
                  <td>
                    <small>{t.transactionSource || "-"}</small>
                  </td>
                  <td>{formatAmount(t.companyWalletBefore)}</td>
                  <td>{formatAmount(t.companyWalletAfter)}</td>
                  <td>{formatAmount(t.branchWalletBefore)}</td>
                  <td>{formatAmount(t.branchWalletAfter)}</td>
                  <td>{t.utrNumber || "-"}</td>
                  <td>{t.referenceId || "-"}</td>
                  <td>{t.remarks || "-"}</td>
                  <td>
                    {t.createdAt
                      ? String(t.createdAt).replace("T", " ").slice(0, 19)
                      : "-"}
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="15" className="text-center text-muted">
                  No transactions found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* ===================== PAGINATION ===================== */}
      {totalPages > 1 && (
        <div className="d-flex justify-content-between align-items-center mt-3">
          <small className="text-muted">
            Total Records: <strong>{totalRecords}</strong>
          </small>

          <div className="d-flex align-items-center gap-1">
            <button
              className="btn btn-sm btn-outline-primary"
              disabled={currentPage === 0}
              onClick={() => fetchTransactions(currentPage - 1)}
            >
              Prev
            </button>

            <span className="mx-2">
              Page {currentPage + 1} of {totalPages}
            </span>

            <button
              className="btn btn-sm btn-outline-primary"
              disabled={currentPage + 1 >= totalPages}
              onClick={() => fetchTransactions(currentPage + 1)}
            >
              Next
            </button>
          </div>
        </div>
      )}

      <ToastContainer
        position="top-right"
        autoClose={3000}
        newestOnTop
        closeOnClick
        pauseOnHover
        draggable
      />
    </div>
  );
};

export default WalletTransactions;
